import { useMemo, useState } from "react";
import Markdown from "@/components/Markdown";
import useWakeLock from "@/hooks/useWakeLock";
import { useBasket } from "@/context/BasketContext";
import { ChefHat, ListChecks, Sun, ClipboardList, Check, ShoppingBasket } from "lucide-react";
import { toast } from "sonner";

/**
 * Parse markdown into { heading, body } sections split at H1/H2/H3 lines.
 * Returns [{ level, title, body }, ...]. Preamble before the first heading is
 * emitted as a section with title=null so we don't lose intro copy.
 */
function parseSections(md = "") {
  const lines = md.split("\n");
  const sections = [];
  let current = { level: 0, title: null, body: [] };
  for (const line of lines) {
    const m = /^(#{1,3})\s+(.+?)\s*#*\s*$/.exec(line);
    if (m) {
      if (current.body.length || current.title) sections.push(current);
      current = { level: m[1].length, title: m[2].trim(), body: [] };
    } else {
      current.body.push(line);
    }
  }
  sections.push(current);
  return sections
    .map((s) => ({ ...s, body: s.body.join("\n").trim() }))
    .filter((s) => s.title || s.body);
}

/**
 * Warm one-tap "hide steps" for Recipe branches. Detects an "ingredients"
 * section by title (case-insensitive substring). If not present, renders
 * the full markdown and shows no toggle.
 */
export default function RecipeContext({ context, category, title, idea }) {
  const [ingredientsOnly, setIngredientsOnly] = useState(false);
  const [justCopied, setJustCopied] = useState(false);
  const basket = useBasket();
  const inBasket = idea && category === "Recipes" && basket ? basket.has(idea.id) : false;

  const sections = useMemo(() => parseSections(context || ""), [context]);
  const ingredientsSection = useMemo(
    () => sections.find((s) => s.title && /ingredient/i.test(s.title)),
    [sections]
  );

  const isRecipe = category === "Recipes";
  const showToggle = isRecipe && !!ingredientsSection;

  // Keep the screen awake while a reader is in ingredients-only mode.
  useWakeLock(ingredientsOnly);

  if (!showToggle) {
    return <Markdown>{context}</Markdown>;
  }

  const ingredientsMd = `## ${ingredientsSection.title}\n\n${ingredientsSection.body}`;

  // Flatten bullets to a plain shopping list (best-effort — keeps sub-numbers if any)
  const shoppingList = () => {
    const lines = ingredientsSection.body.split("\n")
      .map((l) => l.trim())
      .filter(Boolean)
      .map((l) => l.replace(/^([*\-+]|\d+\.)\s+/, "").replace(/[*_`]/g, ""));
    return lines.join("\n");
  };

  const copyIngredients = async () => {
    const header = title ? `${title} — Ingredients\n\n` : "Ingredients\n\n";
    const body = shoppingList();
    const text = `${header}${body}\n\n— from Willoah · real life. real ideas.`;
    try {
      await navigator.clipboard.writeText(text);
      setJustCopied(true);
      toast("Ingredients copied — paste into your notes app.");
      setTimeout(() => setJustCopied(false), 2000);
    } catch {
      toast("Could not copy — try selecting the list manually.");
    }
  };

  return (
    <div>
      <div className="flex flex-wrap items-center gap-2 mb-5" data-testid="recipe-view-toggle">
        <span className="accent-label text-[#B79256] mr-2">view</span>
        <div className="inline-flex rounded-full border border-[#B79256]/35 p-1 bg-white/60">
          <button
            type="button"
            data-testid="view-full"
            onClick={() => setIngredientsOnly(false)}
            className={`accent-label px-3.5 py-1.5 rounded-full inline-flex items-center gap-1.5 transition-colors ${!ingredientsOnly ? "bg-[#4E5A3D] text-[#FBF8F1]" : "text-[#2A2823]/60 hover:text-[#2A2823]"}`}
          >
            <ChefHat className="w-3.5 h-3.5" strokeWidth={1.75} />
            Full recipe
          </button>
          <button
            type="button"
            data-testid="view-ingredients"
            onClick={() => setIngredientsOnly(true)}
            className={`accent-label px-3.5 py-1.5 rounded-full inline-flex items-center gap-1.5 transition-colors ${ingredientsOnly ? "bg-[#4E5A3D] text-[#FBF8F1]" : "text-[#2A2823]/60 hover:text-[#2A2823]"}`}
          >
            <ListChecks className="w-3.5 h-3.5" strokeWidth={1.75} />
            Ingredients only
          </button>
        </div>
        {ingredientsOnly && (
          <span className="inline-flex items-center gap-1.5 text-xs text-[#4E5A3D] accent-label" data-testid="wake-lock-indicator">
            <Sun className="w-3 h-3" strokeWidth={2} /> screen stays awake · steps hidden
          </span>
        )}
        <button
          type="button"
          data-testid="copy-ingredients"
          onClick={copyIngredients}
          className={`ml-auto no-print accent-label inline-flex items-center gap-1.5 px-3.5 py-1.5 rounded-full border transition-colors ${
            justCopied
              ? "bg-[#4E5A3D] text-[#FBF8F1] border-[#4E5A3D]"
              : "bg-white/70 text-[#2A2823] border-[#B79256]/40 hover:bg-[#B79256]/12"
          }`}
          title="Copy ingredients as a shopping list"
        >
          {justCopied ? (
            <><Check className="w-3.5 h-3.5" strokeWidth={2} /> Copied</>
          ) : (
            <><ClipboardList className="w-3.5 h-3.5" strokeWidth={1.75} /> Copy ingredients</>
          )}
        </button>
        {idea && (
          <button
            type="button"
            data-testid="add-to-basket"
            onClick={() => (inBasket ? basket.remove(idea.id) : basket.add(idea))}
            className={`no-print accent-label inline-flex items-center gap-1.5 px-3.5 py-1.5 rounded-full border transition-colors ${
              inBasket
                ? "bg-[#B79256] text-[#FBF8F1] border-[#B79256] hover:bg-[#a4823d]"
                : "bg-white/70 text-[#2A2823] border-[#B79256]/40 hover:bg-[#B79256]/12"
            }`}
            title={inBasket ? "In basket — tap to remove" : "Add to meal plan basket"}
          >
            {inBasket ? <><Check className="w-3.5 h-3.5" strokeWidth={2}/> In basket</> : <><ShoppingBasket className="w-3.5 h-3.5" strokeWidth={1.75}/> Add to basket</>}
          </button>
        )}
      </div>

      {ingredientsOnly ? (
        <div data-testid="ingredients-only">
          <Markdown>{ingredientsMd}</Markdown>
        </div>
      ) : (
        <Markdown>{context}</Markdown>
      )}
    </div>
  );
}
